/**
 * Queries and mutations of IT Admin → Knowledge (`/knowledge/*`, 9 Oct).
 *
 * Every change re-reads everything under `knowledgeKeys.all`: the list, the
 * overview, an article and its history move together.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  commentKnowledgeArticle as sdkComment,
  commitKnowledgeImport as sdkCommitImport,
  createKnowledgeArea as sdkCreateArea,
  createKnowledgeArticle as sdkCreateArticle,
  getKnowledgeArticle as sdkGetArticle,
  getKnowledgeImport as sdkGetImport,
  getKnowledgeOverview as sdkGetOverview,
  type KnowledgeArea,
  type KnowledgeAreaCreate,
  type KnowledgeAreaList,
  type KnowledgeAreaUpdate,
  type KnowledgeArticle,
  type KnowledgeArticleInput,
  type KnowledgeArticleList,
  type KnowledgeArticleUpdate,
  type KnowledgeImportBatch,
  type KnowledgeImportRequest,
  type KnowledgeOverview,
  type KnowledgePreviewResult,
  type KnowledgeReadArticle,
  type KnowledgeReindexResult,
  type KnowledgeStatusChange,
  type KnowledgeUploadFile,
  type KnowledgeVersionList,
  listKnowledgeAreas as sdkListAreas,
  listKnowledgeArticles as sdkListArticles,
  listKnowledgeVersions as sdkListVersions,
  previewKnowledgeImport as sdkPreviewImport,
  readKnowledgeArticle as sdkReadArticle,
  reindexKnowledge as sdkReindexAll,
  reindexKnowledgeArticle as sdkReindexArticle,
  setKnowledgeArticleStatus as sdkSetStatus,
  updateKnowledgeArea as sdkUpdateArea,
  updateKnowledgeArticle as sdkUpdateArticle,
} from "@/lib/api";
import { logger } from "@/lib/logger";

import { type ListFilters, listQuery } from "./lib/knowledge";

export const knowledgeKeys = {
  all: ["knowledge"] as const,
  overview: () => [...knowledgeKeys.all, "overview"] as const,
  list: (filters: Record<string, string>) => [...knowledgeKeys.all, "list", filters] as const,
  article: (id: string) => [...knowledgeKeys.all, "article", id] as const,
  versions: (id: string) => [...knowledgeKeys.all, "versions", id] as const,
  areas: () => [...knowledgeKeys.all, "areas"] as const,
  batch: (id: string) => [...knowledgeKeys.all, "batch", id] as const,
  read: (id: string) => ["knowledge-read", id] as const,
};

/** Plain same-origin links, so the browser downloads them. */
export const KNOWLEDGE_EXPORT_ZIP = "/api/knowledge/export?format=zip";
export const KNOWLEDGE_EXPORT_XLSX = "/api/knowledge/export?format=xlsx";

export function useKnowledgeOverview() {
  return useQuery({
    queryKey: knowledgeKeys.overview(),
    queryFn: async (): Promise<KnowledgeOverview> => {
      const { data } = await sdkGetOverview({ throwOnError: true });
      return data;
    },
    staleTime: 30_000,
  });
}

export function useKnowledgeArticles(filters: ListFilters) {
  const query = listQuery(filters);
  return useQuery({
    queryKey: knowledgeKeys.list(query),
    queryFn: async (): Promise<KnowledgeArticleList> => {
      const { data } = await sdkListArticles({ query, throwOnError: true });
      return data;
    },
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

export function useKnowledgeArticle(id: string | undefined) {
  return useQuery({
    queryKey: knowledgeKeys.article(id ?? ""),
    queryFn: async (): Promise<KnowledgeArticle> => {
      const { data } = await sdkGetArticle({ path: { article_id: id ?? "" }, throwOnError: true });
      return data;
    },
    enabled: id !== undefined && id !== "new",
  });
}

export function useKnowledgeVersions(id: string, enabled: boolean) {
  return useQuery({
    queryKey: knowledgeKeys.versions(id),
    queryFn: async (): Promise<KnowledgeVersionList> => {
      const { data } = await sdkListVersions({ path: { article_id: id }, throwOnError: true });
      return data;
    },
    enabled,
  });
}

export function useKnowledgeAreas() {
  return useQuery({
    queryKey: knowledgeKeys.areas(),
    queryFn: async (): Promise<KnowledgeAreaList> => {
      const { data } = await sdkListAreas({ throwOnError: true });
      return data;
    },
    staleTime: 60_000,
  });
}

/** The read-only page a chat citation opens (any signed-in person). */
export function useReadKnowledgeArticle(id: string | undefined) {
  return useQuery({
    queryKey: knowledgeKeys.read(id ?? ""),
    queryFn: async (): Promise<KnowledgeReadArticle> => {
      const { data } = await sdkReadArticle({ path: { article_id: id ?? "" }, throwOnError: true });
      return data;
    },
    enabled: id !== undefined,
    retry: false,
  });
}

/** A bulk add's indexing progress, polled while it runs. */
export function useKnowledgeBatch(id: string | null) {
  return useQuery({
    queryKey: knowledgeKeys.batch(id ?? ""),
    queryFn: async (): Promise<KnowledgeImportBatch> => {
      const { data } = await sdkGetImport({ path: { batch_id: id ?? "" }, throwOnError: true });
      return data;
    },
    enabled: id !== null,
    refetchInterval: (query) => (query.state.data?.result === "indexing" ? 2000 : false),
  });
}

function useInvalidateAll() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: knowledgeKeys.all });
  };
}

export function useCreateKnowledgeArticle() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: async (body: KnowledgeArticleInput): Promise<KnowledgeArticle> => {
      logger.debug("[knowledge] create", body.title);
      const { data } = await sdkCreateArticle({ body, throwOnError: true });
      return data;
    },
    onSettled: invalidate,
  });
}

export function useUpdateKnowledgeArticle() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: string;
      body: KnowledgeArticleUpdate;
    }): Promise<KnowledgeArticle> => {
      logger.debug("[knowledge] update", id, body.version);
      const { data } = await sdkUpdateArticle({
        path: { article_id: id },
        body,
        throwOnError: true,
      });
      return data;
    },
    onSettled: invalidate,
  });
}

export function useSetKnowledgeStatus() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: string;
      body: KnowledgeStatusChange;
    }): Promise<KnowledgeArticle> => {
      logger.debug("[knowledge] status", id, body.action);
      const { data } = await sdkSetStatus({ path: { article_id: id }, body, throwOnError: true });
      return data;
    },
    onSettled: invalidate,
  });
}

export function useReindexKnowledgeArticle() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: async (id: string): Promise<KnowledgeArticle> => {
      const { data } = await sdkReindexArticle({ path: { article_id: id }, throwOnError: true });
      return data;
    },
    onSettled: invalidate,
  });
}

export function useReindexKnowledge() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: async (): Promise<KnowledgeReindexResult> => {
      logger.debug("[knowledge] reindex all");
      const { data } = await sdkReindexAll({ throwOnError: true });
      return data;
    },
    onSettled: invalidate,
  });
}

export function useCommentKnowledgeArticle() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: async ({ id, comment }: { id: string; comment: string }) => {
      const { data } = await sdkComment({
        path: { article_id: id },
        body: { comment },
        throwOnError: true,
      });
      return data;
    },
    onSettled: invalidate,
  });
}

/** Read dropped files (one request); nothing is saved. */
export async function previewKnowledgeFiles(
  files: KnowledgeUploadFile[],
): Promise<KnowledgePreviewResult> {
  logger.debug("[knowledge] preview", files.length);
  const { data } = await sdkPreviewImport({ body: { files }, throwOnError: true });
  return data;
}

export function useCommitKnowledgeImport() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: async (body: KnowledgeImportRequest): Promise<KnowledgeImportBatch> => {
      logger.debug("[knowledge] commit", body.mode, body.items.length);
      const { data } = await sdkCommitImport({ body, throwOnError: true });
      return data;
    },
    onSettled: invalidate,
  });
}

export function useCreateKnowledgeArea() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: async (body: KnowledgeAreaCreate): Promise<KnowledgeArea> => {
      const { data } = await sdkCreateArea({ body, throwOnError: true });
      return data;
    },
    onSettled: invalidate,
  });
}

export function useUpdateKnowledgeArea() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: string;
      body: KnowledgeAreaUpdate;
    }): Promise<KnowledgeArea> => {
      const { data } = await sdkUpdateArea({ path: { area_id: id }, body, throwOnError: true });
      return data;
    },
    onSettled: invalidate,
  });
}

/** A file's bytes as base64, for the JSON body. */
export async function fileToBase64(file: Blob): Promise<string> {
  const buffer = new Uint8Array(await file.arrayBuffer());
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < buffer.length; i += chunk) {
    binary += String.fromCharCode(...buffer.subarray(i, i + chunk));
  }
  return btoa(binary);
}
